import Resumable from 'resumablejs';

import { isNullOrWhitespace } from '@pressreader/utils';

import { authorized } from '../authorization';
import { httpPost, httpPut } from '../http';

import { FileNotFound, SelectFileCancelled } from './errors';

type Headers = {
    Authorization?: string;
} & Record<string, string>;

type ConfigurationHash = Resumable.ConfigurationHash;

export interface Options extends ConfigurationHash {
    permanentErrors?: number[];
    headers?: Headers;
    allowedExtensions?: string;
}

export interface FileUploadResult {
    fileId: string;
    contentType?: string;
}
const defaultOptions: Options = {
    testChunks: false,
    // The type is defined as boolean for some reason. Looks like the limits are actually not implemented.
    minFileSize: 1 as unknown as boolean,
    permanentErrors: [0, 400, 403, 404, 413, 415, 500, 501],
    headers: {},
};

/**
 * Creates new file input.
 */
function createFileInput(allowedExtensions?: string) {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.style.opacity = '0';
    fileInput.style.display = 'none';
    if (allowedExtensions && !isNullOrWhitespace(allowedExtensions)) {
        fileInput.setAttribute('accept', allowedExtensions);
    }
    return fileInput;
}

/**
 * Opens new file dialog.
 */
function openDialog({ allowedExtensions }: Options) {
    return new Promise<File>((resolve, reject) => {
        const fileInput = createFileInput(allowedExtensions);
        fileInput.addEventListener('input', (event: Event) => {
            const file = (event.target as HTMLInputElement)?.files?.[0];
            if (file) {
                resolve(file);
            } else {
                reject(new FileNotFound('Select file not found'));
            }
        });
        fileInput.addEventListener('cancel', () => {
            reject(new SelectFileCancelled('Select file dialog cancelled.'));
        });
        fileInput.focus();
        fileInput.dispatchEvent(new MouseEvent('click'));
    });
}

function createResumable(options: Options) {
    const resumable = new Resumable(options);
    if (!resumable.support) {
        throw new Error('Resumable.js not supported');
    }

    return resumable;
}

/**
 * Creates a new file on the server and returns its id.
 */
async function createFile(createFileUrl: string, file: File, resumable: Resumable) {
    const { id } = await httpPost<{ id: string }>({
        url: createFileUrl,
        data: { contentType: file.type },
        requestConfig: {
            contentType: 'application/json',
        },
    });
    return id;
}

/**
 * Uploads file to server
 */
function startUploading(resumable: Resumable, file: File) {
    return new Promise<void>((resolve, reject) => {
        resumable.on('complete', () => {
            resolve();
        });

        resumable.on('fileError', (file, message) => {
            reject(new Error(message));
        });

        resumable.on('fileAdded', () => {
            resumable.upload();
        });

        resumable.addFile(file);
    });
}

function finalizeUploading<TResult extends FileUploadResult>(url: string) {
    return httpPut<TResult>({ url });
}

export async function uploadFile<TResult extends FileUploadResult>(params: { serverUrl: string; storage: string; options?: Options }) {
    const { serverUrl, storage } = params;
    const options: Options = { ...defaultOptions, ...params.options };

    const token = await authorized();
    options.headers = { ...options.headers, Authorization: `Bearer ${token}` };

    const createFileUrl = `${serverUrl}/create/${storage}`;

    const resumable = createResumable(options);
    const file = await openDialog({ allowedExtensions: options.allowedExtensions });
    const id = await createFile(createFileUrl, file, resumable);

    resumable.opts.target = `${serverUrl}/append/${storage}/${id}`;
    await startUploading(resumable, file);

    return await finalizeUploading<TResult>(`${serverUrl}/finalize/${storage}/${id}`);
}
